import { TIPO_ORIGEN } from 'src/components/views/EscritosLexnet/components/IniciadorAsunto/components/Origen/consts'
import { EscritosFormValues } from 'src/components/views/EscritosLexnet/components/shared/types/escritosFormTypes'
import { LEXNET_MAX_MAIN_FILE_SIZE } from 'src/constants/lexnet'
import { CautelarOpciones, ResolucionOpciones } from 'src/enums/EscritosLegales'
import { FormField } from 'src/enums/Form'
import { OrganoJudicial, PartidoJudicial } from 'src/interfaces/Catalog'
import { Escrito, Nig } from 'src/interfaces/EscritosLegales/index'

const getValue = (value, field, defaultValue: any = '') => {
  if (value) {
    return value[field]
  }
  return defaultValue
}

// DATOS PRINCIPALES
export function principalMapping(escritoForm: EscritosFormValues, escrito: Escrito): void {
  escritoForm[FormField.ID] = escrito.id
  escritoForm[FormField.TIPO] = getValue(escrito, 'tipo', null)
  escritoForm[FormField.TIPO_ESCRITO] = getValue(escrito, 'tipoEscrito', null)
}

// SUBSANACIÓN / RECHAZADOS
export function subsanacionMapping(escritoForm: EscritosFormValues, escrito: Escrito): void {
  escritoForm[FormField.ES_CORRECCION] = escrito.esCorreccion ?? false
  escritoForm[FormField.ID_MENSAJE_CORRECCION] = escrito.idMensajeCorreccion
    ? escrito.idMensajeCorreccion.toString()
    : '0'
}

// ÓRGANO Y ORDEN
export function organoDestinoOrdenMapping(escritoForm: EscritosFormValues, escrito: Escrito): void {
  const organo: OrganoJudicial | null = getValue(escrito.destino, 'organo', null)

  if (!organo) {
    return
  }

  let descripcion = organo.nombre

  const partido: PartidoJudicial = getValue(escrito.destino, 'partido', '')

  if (partido) {
    descripcion = `${descripcion} DE ${partido.nombrePartido.toUpperCase()}`
  }

  escritoForm[FormField.ORGANO_DESTINO_DESCRIPCION] = descripcion
  escritoForm[FormField.ORGANO_DESTINO_ORDEN_DESCRIPCION] = getValue(
    escrito.orden,
    'descripcion',
    ''
  )

  escritoForm[FormField.ORGANO_DESTINO] = {
    [FormField.ORGANO_DESTINO_ORDEN_JUDICIAL]: getValue(escrito.destino, 'orden', null),
    [FormField.ORGANO_DESTINO_ORGANO_JUDICIAL]: getValue(escrito.destino, 'organo', null),
    [FormField.ORGANO_DESTINO_PARTIDO_JUDICIAL]: getValue(escrito.destino, 'partido', null),
    [FormField.COMUNIDAD_AUTONOMA]: getValue(escrito.destino, 'comunidad', null),
    [FormField.PROVINCIA]: getValue(escrito.destino, 'provincia', null)
  }

  escritoForm[FormField.ORGANO_DESTINO_ENVIAR_JUZGADO_GUARDIA] = getValue(
    escrito.destino,
    'juzgadoGuardia',
    false
  )

  escritoForm[FormField.DATOS_MERCANTIL] = getValue(escrito.datos, 'mercantil', false)

  escritoForm[FormField.ORGANO_DESTINO_DE_TITULO_JUDICIAL] = escrito.organoDestinoDeTituloJudicial
}

// ÓRGANO ORIGEN - ÓRGANO Y ORDEN
export function organoOrigenOrdenMapping(escritoForm: EscritosFormValues, escrito: Escrito): void {
  escritoForm[FormField.CAUTELAR] =
    escrito && escrito.cautelar ? CautelarOpciones.SI : CautelarOpciones.NO

  const organoOrigen: OrganoJudicial | null =
    escrito.origen && escrito.origen.organo ? escrito.origen.organo : null
  let descripcion = getValue(organoOrigen, 'nombre', '')
  const partido: PartidoJudicial = getValue(escrito.origen, 'partido', '')

  if (partido) {
    descripcion = `${descripcion} DE ${partido.nombrePartido.toUpperCase()}`
  }

  escritoForm[FormField.ORGANO_ORIGEN_DESCRIPCION] = descripcion
  escritoForm[FormField.ORGANO_ORIGEN_ORDEN_DESCRIPCION] = getValue(
    escrito.origen?.orden,
    'descripcion',
    ''
  )
  if (escrito.origen && escrito.organoOrigen) {
    escritoForm[FormField.ORGANO_ORIGEN] = {
      [FormField.ORGANO_ORIGEN_ORDEN_JUDICIAL]: getValue(escrito.origen, 'orden', null),
      [FormField.ORGANO_ORIGEN_ORGANO_JUDICIAL]: getValue(escrito.origen, 'organo', null),
      [FormField.ORGANO_ORIGEN_PARTIDO_JUDICIAL]: getValue(escrito.origen, 'partido', null),
      [FormField.ORGANO_ORIGEN_COMUNIDAD_AUTONOMA]: getValue(
        escrito.organoOrigen,
        'comunidad',
        null
      ),
      [FormField.ORGANO_ORIGEN_PROVINCIA]: getValue(escrito.organoOrigen, 'provincia', null)
    }
  }

  const mappingOrganoOrigenTipo = (value): TIPO_ORIGEN | null => {
    switch (value) {
      case 0:
        return TIPO_ORIGEN.JUDICIAL
      case 1:
        return TIPO_ORIGEN.EXTRAJUDICIAL
      default:
        return null
    }
  }

  escritoForm[FormField.ORGANO_ORIGEN_TIPO_ORIGEN] = mappingOrganoOrigenTipo(
    escrito.organoOrigenTipo
  )
}

// ORGANISMO ORIGEN
export function organismoOrigenMapping(escritoForm: EscritosFormValues, escrito: Escrito): void {
  escritoForm[FormField.ORGANO_ORIGEN_ORGANISMO_ORIGEN] = getValue(
    escrito.organismoOrigen,
    'organismoOrigen',
    null
  )

  escritoForm[FormField.ORGANO_ORIGEN_NUMERO_EXPEDIENTE] = getValue(
    escrito.organismoOrigen,
    'numeroExpediente',
    null
  )

  escritoForm[FormField.ORGANO_ORIGEN_YEAR_EXPEDIENTE] = getValue(
    escrito.organismoOrigen,
    'yearExpediente',
    null
  )
}

// DATOS: NIG
export function datosNIGMapping(escritoForm: EscritosFormValues, escrito: Escrito): void {
  const nig: Nig = getValue(escrito, 'NIG', null)

  if (!nig) {
    return
  }

  escritoForm[FormField.NIG] = nig.value
  escritoForm[FormField.NIG_1] = nig.N1
  escritoForm[FormField.NIG_2] = nig.N2
  escritoForm[FormField.NIG_3] = nig.N3
  escritoForm[FormField.NIG_4] = nig.N4
  escritoForm[FormField.NIG_5] = nig.N5
  escritoForm[FormField.NIG_FULL_NUMBER] = nig.value
}

// DATOS: RECONVENCIÓN
export function datosReconvencionNIGMapping(
  escritoForm: EscritosFormValues,
  escrito: Escrito
): void {
  const esReconvencion: boolean = getValue(escrito.datos, 'esReconvencion', false)

  if (!esReconvencion) {
    return
  }

  const esReconvencionTexto = esReconvencion ? 'Sí' : 'No'

  escritoForm[FormField.DATOS_RECONVENCION] = esReconvencionTexto
}

export function datosCuantiaMapping(escritoForm: EscritosFormValues, escrito: Escrito): void {
  if (escrito && escrito.datos && escrito.datos.cuantia) {
    escritoForm[FormField.DATOS_CUANTIA_RECONVENCION] = getValue(
      escrito.datos.cuantia,
      'cuantiaReconvencion',
      0
    )
    escritoForm[FormField.DATOS_TIPO_CUANTIA] = getValue(escrito.datos.cuantia, 'tipoCuantia', null)
    escritoForm[FormField.DATOS_CUANTIA_PRINCIPAL] = getValue(
      escrito.datos.cuantia,
      'cuantiaPrincipal'
    )

    escritoForm[FormField.DATOS_CUANTIA_INTERESES] = getValue(escrito.datos.cuantia, 'intereses')

    escritoForm[FormField.DATOS_CUANTIA_COSTAS] = getValue(escrito.datos.cuantia, 'costas')

    escritoForm[FormField.DATOS_CUANTIA_INTERESES_REMUNERATORIOS] = getValue(
      escrito.datos.cuantia,
      'interesesRemuneratorios'
    )

    escritoForm[FormField.DATOS_CUANTIA_INTERESES_DEMORA] = getValue(
      escrito.datos.cuantia,
      'interesesDemora'
    )

    escritoForm[FormField.DATOS_CUANTIA_COMISION_IMPAGO_RECIBOS] = getValue(
      escrito.datos.cuantia,
      'comision'
    )

    escritoForm[FormField.DATOS_CUANTIA_PENALIZACION_RETRASO_PAGO] = getValue(
      escrito.datos.cuantia,
      'penalizacion'
    )
  }
}

// DATOS
export function datosMapping(escritoForm: EscritosFormValues, escrito: Escrito): void {
  datosNIGMapping(escritoForm, escrito)

  datosReconvencionNIGMapping(escritoForm, escrito)
  datosCuantiaMapping(escritoForm, escrito)

  escritoForm[FormField.DATOS_URGENTE] = getValue(escrito.datos, 'urgente', false)
  escritoForm[FormField.DATOS_OBSERVACIONES] = getValue(escrito.datos, 'observaciones', '')
  escritoForm[FormField.DATOS_ORIGEN_DEUDA] = getValue(escrito.datos, 'origenDeuda', '')
  escritoForm[FormField.DATOS_FUNDAMENTOS_DERECHO] = getValue(
    escrito.datos,
    'fundamentosMonitorio',
    ''
  )
  escritoForm[FormField.DATOS_PETICION] = getValue(escrito.datos, 'peticionMonitorio', '')
  escritoForm[FormField.DATOS_PETICION_COMPLEMENTARIA] = getValue(
    escrito.datos,
    'peticionComplementariaMonitorio',
    ''
  )
  escritoForm[FormField.DATOS_RECLAMACION_CONSUMIDOR] = getValue(
    escrito.datos,
    'reclamacionConsumidor',
    false
  )
  escritoForm[FormField.DATOS_RECLAMACION_COMUNIDAD] = getValue(
    escrito.datos,
    'reclamacionComunidad',
    false
  )
  escritoForm[FormField.DATOS_DEUDA_PARTIDO_JUDICIAL] = getValue(
    escrito.datos,
    'deudaPartidoJudicial',
    false
  )

  escritoForm[FormField.DATOS_MASC] = getValue(escrito.datos, 'masc', true)

  escritoForm[FormField.DATOS_MATERIA] = getValue(escrito.datos, 'materia', null)

  escritoForm[FormField.DATOS_MEDIDA_CAUTELAR_PREVIA] = getValue(
    escrito.datos,
    'medidaCautelarPrevia',
    false
  )
  escritoForm[FormField.DATOS_MEDIDA_CAUTELAR_COETANEA] = getValue(
    escrito.datos,
    'medidaCautelarCoetanea',
    false
  )
  escritoForm[FormField.DATOS_VIOLENCIA_GENERO] = getValue(
    escrito.datos,
    'violenciaDeGenero',
    false
  )
  escritoForm[FormField.DATOS_CAUSA_CON_DETENIDO] = getValue(
    escrito.datos,
    'causaConDetenido',
    false
  )
  escritoForm[FormField.DATOS_CAUSA_CON_PRESO] = getValue(escrito.datos, 'causaConPreso', false)
  escritoForm[FormField.DATOS_FECHA_ACUERDO] = getValue(escrito.datos, 'datosFechaAcuerdo', null)
  escritoForm[FormField.DATOS_OBJETO_RECURSO] = getValue(escrito.datos, 'objetoRecurso')
  escritoForm[FormField.DATOS_ORGANISMO] = getValue(escrito.datos, 'datosOrganismo')
  escritoForm[FormField.DATOS_ORGANISMO_ORIGEN] = getValue(escrito.datos, 'datosOrganismoOrigen')
  escritoForm[FormField.DATOS_NUMERO_EXPEDIENTE_ORIGEN] = getValue(
    escrito.datos,
    'numeroExpedienteOrigen'
  )
  escritoForm[FormField.DATOS_YEAR_EXPEDIENTE_ORIGEN] = getValue(
    escrito.datos,
    'yearExpedienteOrigen'
  )
  escritoForm[FormField.DATOS_CAUTELARISIMA] = getValue(escrito.datos, 'datosCautelarisima', false)

  escritoForm[FormField.DATOS_DELITO] = getValue(escrito.datos, 'delito', null)
  escritoForm[FormField.DATOS_LOCALIDAD] = getValue(escrito.datos, 'datosLocalidad')
  escritoForm[FormField.DATOS_FECHA] = getValue(escrito.datos, 'datosFecha')
  escritoForm[FormField.DATOS_HORA] = getValue(escrito.datos, 'datosHora')

  escritoForm[FormField.INTERVINIENTE_INFORMAR_FISCALIA] = escrito.informarFiscalia ?? false
}

// RESOLUCIÓN
export function resolucionMapping(escritoForm: EscritosFormValues, escrito: Escrito): void {
  escritoForm[FormField.RESOLUCION] = getValue(
    escrito.resolucion,
    'informarDatos',
    ResolucionOpciones.NO
  )
  escritoForm[FormField.TIPO_RESOLUCION] = getValue(escrito.resolucion, 'tipoResolucion', null)
  escritoForm[FormField.FECHA_RESOLUCION] = getValue(escrito.resolucion, 'fechaResolucion', null)
  escritoForm[FormField.NUMERO_RESOLUCION] = getValue(escrito.resolucion, 'numeroResolucion')
  escritoForm[FormField.YEAR_RESOLUCION] = getValue(escrito.resolucion, 'yearResolucion')
  escritoForm[FormField.RESOLUCION_BIS] = getValue(escrito.resolucion, 'resolucionBis', false)
}

// PROCEDIMIENTO
export function procedimientoMapping(escritoForm: EscritosFormValues, escrito: Escrito): void {
  if (!escrito.procedimiento) {
    return
  }

  escritoForm[FormField.PROCEDIMIENTO_DESCRIPCION] = escrito.procedimiento.procedimiento
    ? escrito.procedimiento.procedimiento.texto
    : ''
  escritoForm[FormField.PROCEDIMIENTO_ORIGEN] = escrito.procedimiento.procedimientoOrigen
    ? escrito.procedimiento.procedimientoOrigen.texto
    : ''
  escritoForm[FormField.PROCEDIMIENTO_NUMERO_ORIGEN_FORM] =
    escrito.procedimiento.numeroProcedimiento
  escritoForm[FormField.PROCEDIMIENTO_REFERENCIA] = escrito.procedimiento.numeroProcedimiento
  escritoForm[FormField.PROCEDIMIENTO_NUMERO_FORM] = escrito.procedimiento.numeroProcedimiento
    ? escrito.procedimiento.numeroProcedimiento.split('/')[0]
    : ''
  escritoForm[FormField.PROCEDIMIENTO_YEAR_FORM] = escrito.procedimiento.numeroProcedimiento
    ? escrito.procedimiento.numeroProcedimiento.split('/')[1]
    : ''
  escritoForm[FormField.PROCEDIMIENTO_ORIGEN_REFERENCIA] = escrito.procedimiento.numeroProcedimiento
  escritoForm[FormField.PROCEDIMIENTO_ORIGEN_REFERENCIA] = escrito.procedimiento.numeroProcedimiento
  escritoForm[FormField.PROCEDIMIENTO_NUMERO_PIEZA] = escrito.procedimiento.numeroPieza
    ? escrito.procedimiento.numeroPieza
    : ''
  escritoForm[FormField.PROCEDIMIENTO] = {
    [FormField.PROCEDIMIENTO_NUMERO_FORM]: escritoForm[FormField.PROCEDIMIENTO_NUMERO_FORM],
    [FormField.PROCEDIMIENTO_NUMERO_ORIGEN_FORM]: escrito.procedimiento.numeroProcedimientoOrigen
      ? escrito.procedimiento.numeroProcedimientoOrigen.split('/')[0]
      : '',
    [FormField.PROCEDIMIENTO_NUMERO_PIEZA_FORM]: escrito.procedimiento.numeroPieza
      ? escrito.procedimiento.numeroPieza
      : '',
    [FormField.PROCEDIMIENTO_TIPOLOGIA_FORM]: escrito.procedimiento.tipoProcedimiento,
    [FormField.PROCEDIMIENTO_TIPO_FORM]: escrito.procedimiento.procedimiento,
    [FormField.PROCEDIMIENTO_TIPO_ORIGEN_FORM]: escrito.procedimiento.procedimientoOrigen,
    [FormField.PROCEDIMIENTO_YEAR_FORM]: escritoForm[FormField.PROCEDIMIENTO_YEAR_FORM],
    [FormField.PROCEDIMIENTO_YEAR_ORIGEN_FORM]: escrito.procedimiento.numeroProcedimientoOrigen
      ? escrito.procedimiento.numeroProcedimientoOrigen.split('/')[1]
      : ''
  }
}

// DOCUMENTO PRINCIPAL Y ADJUNTOS
export function adjuntosMapping(escritoForm: EscritosFormValues, escrito: Escrito): void {
  escritoForm[FormField.DOCUMENTO_PRINCIPAL] = escrito.documentoPrincipal
  escritoForm[FormField.DOCUMENTO_PRINCIPAL_ARCHIVO] = {
    file: escrito.documentoPrincipalArchivo ? escrito.documentoPrincipalArchivo : new File([], ''),
    hash: escrito.documentoPrincipalArchivo ? escrito.documentoPrincipal[0].hash : null
  }
  const mainFileSize = escrito.documentoPrincipalArchivo?.size || 0
  escritoForm[FormField.EXCESO_CABIDA] = mainFileSize > LEXNET_MAX_MAIN_FILE_SIZE
}

// INTERVINIENTES
export function intervinientesMapping(escritoForm: EscritosFormValues, escrito: Escrito): void {
  if (escrito.intervinientes) {
    escritoForm[FormField.INTERVINIENTES] = escrito.intervinientes
  }
  escritoForm[FormField.INTERVINIENTES_FROM_LEXON_VALIDATED] =
    escrito.intervinientesFromLexonValidated
}

// TRASLADO DE COPIAS
export function trasladoCopiasMapping(escritoForm: EscritosFormValues, escrito: Escrito): void {
  escritoForm[FormField.TRASLADO_COPIAS] = escrito.trasladoCopia ?? []
}
